import { useCallback, useRef, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { FormDialog } from '@xhr-labs/core-ui';
import { Badge, Box, Button, Divider, Group, Menu, Stack, Text } from '@mantine/core';
import {
  IconChevronDown,
  IconLogout,
  IconMenu2,
  IconSettings,
  IconShieldLock,
  IconUserCircle,
} from '@tabler/icons-react';
import { useAuth } from '@/modules/auth/use-auth';
import { PasswordChangeForm } from '@/modules/settings/PasswordChangeForm';
import { showSuccessNotification } from '@/lib/notifications';

interface HeaderProps {
  onMenuClick: () => void;
}

export function Header({ onMenuClick }: HeaderProps) {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const [isPasswordDialogOpen, setIsPasswordDialogOpen] = useState(false);
  const currentPasswordInputRef = useRef<HTMLInputElement>(null);

  const focusCurrentPasswordInput = useCallback(() => {
    currentPasswordInputRef.current?.focus();
  }, []);

  const handlePasswordChanged = () => {
    setIsPasswordDialogOpen(false);
    showSuccessNotification({
      title: 'Password updated',
      message: 'Your password was changed successfully.',
    });
  };

  if (!user) {
    throw new Error('Header requires authenticated user');
  }

  const handleLogout = () => {
    logout();
    void navigate('/login');
  };

  const heading = getHeaderCopy(location.pathname);

  return (
    <Box
      h="100%"
      px="lg"
      style={{
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between',
        borderBottom: '1px solid rgba(148, 163, 184, 0.16)',
        backgroundColor: 'transparent',
      }}
    >
      <Group gap="md">
        <Button
          variant="default"
          size="sm"
          onClick={onMenuClick}
          aria-label="Toggle menu"
          style={{
            border: '1px solid var(--xhr-color-border-neutral-light)',
            backgroundColor: 'var(--xhr-color-bg-surface)',
            color: 'var(--xhr-color-content-neutral-medium)',
          }}
        >
          <IconMenu2 size={18} />
        </Button>

        <Stack gap={0}>
          <Group gap="xs" align="center">
            <Text fw={700} size="lg" c="var(--xhr-color-content-neutral-strong)">
              {heading.title}
            </Text>
            <Badge
              variant="light"
              color="blue"
              radius="sm"
              styles={{
                root: {
                  textTransform: 'none',
                },
              }}
            >
              Admin
            </Badge>
          </Group>
          <Text size="sm" c="dimmed">
            {heading.description}
          </Text>
        </Stack>
      </Group>

      <Group gap="sm">
        <Badge
          variant="light"
          color="green"
          styles={{
            root: {
              textTransform: 'none',
              paddingInline: '10px',
              height: 30,
            },
          }}
        >
          Live workspace
        </Badge>

        <Menu shadow="md" width={220} position="bottom-end" returnFocus={!isPasswordDialogOpen}>
          <Menu.Target>
            <Button
              variant="subtle"
              size="sm"
              leftSection={<IconUserCircle size={18} />}
              rightSection={
                <IconChevronDown size={14} color="var(--xhr-color-content-neutral-medium)" />
              }
              style={{
                border: '1px solid var(--xhr-color-border-neutral-light)',
                backgroundColor: 'var(--xhr-color-bg-surface)',
                color: 'var(--xhr-color-content-neutral-strong)',
              }}
            >
              <Text size="sm" style={{ maxWidth: 150 }} truncate>
                {user.email}
              </Text>
            </Button>
          </Menu.Target>

          <Menu.Dropdown>
            <Box p="sm">
              <Group gap="sm">
                <IconUserCircle size={20} />
                <Text size="sm" fw={500}>
                  {user.email}
                </Text>
              </Group>
            </Box>

            <Divider />

            <Menu.Item leftSection={<IconSettings size={16} />} disabled>
              Profile Settings
            </Menu.Item>
            <Menu.Item
              leftSection={<IconShieldLock size={16} />}
              onClick={() => {
                setIsPasswordDialogOpen(true);
              }}
            >
              Change Password
            </Menu.Item>

            <Divider />

            <Menu.Item leftSection={<IconLogout size={16} />} color="red" onClick={handleLogout}>
              Logout
            </Menu.Item>
          </Menu.Dropdown>
        </Menu>
      </Group>

      <FormDialog
        isOpen={isPasswordDialogOpen}
        onClose={() => {
          setIsPasswordDialogOpen(false);
        }}
        title="Change Password"
        size="sm"
        initialFocusRef={currentPasswordInputRef}
        onOpened={focusCurrentPasswordInput}
      >
        {isPasswordDialogOpen && (
          <PasswordChangeForm
            currentPasswordInputRef={currentPasswordInputRef}
            onSuccess={handlePasswordChanged}
          />
        )}
      </FormDialog>
    </Box>
  );
}

function getHeaderCopy(pathname: string): { title: string; description: string } {
  if (pathname.startsWith('/support/god-admin')) {
    return {
      title: 'God Admin',
      description: 'Manage support access to customer workspaces.',
    };
  }

  if (pathname.startsWith('/support/tickets')) {
    return {
      title: 'Support Tickets',
      description: 'Monitor open issues, assignment load, and SLA risk.',
    };
  }

  if (pathname.startsWith('/support/categories')) {
    return {
      title: 'Support Categories',
      description: 'Manage how tickets are classified and routed.',
    };
  }

  if (pathname.startsWith('/support/queues')) {
    return {
      title: 'Support Queues',
      description: 'Balance workload across response queues and owners.',
    };
  }

  if (pathname.startsWith('/support/agents')) {
    return {
      title: 'Support Agents',
      description: 'Review staffing, ownership, and agent availability.',
    };
  }

  if (pathname.startsWith('/support/reports')) {
    return {
      title: 'Support Reports',
      description: 'Track throughput, backlog, and service health trends.',
    };
  }

  if (pathname.startsWith('/dashboard')) {
    return {
      title: 'Operations Dashboard',
      description: 'Prioritize the queues, approvals, and admin actions that need attention now.',
    };
  }

  if (pathname.startsWith('/users')) {
    return {
      title: 'Users',
      description: 'Manage administrator access and user account lifecycle.',
    };
  }

  if (pathname.startsWith('/companies')) {
    return {
      title: 'Companies',
      description: 'Review tenant footprint, status, and onboarding progress.',
    };
  }

  if (pathname.startsWith('/audit')) {
    return {
      title: 'Audit',
      description: 'Inspect administrative events, changes, and traceability.',
    };
  }

  if (pathname.startsWith('/settings')) {
    return {
      title: 'Settings',
      description: 'Configure platform-wide administrative preferences.',
    };
  }

  if (pathname.startsWith('/appstore')) {
    return {
      title: 'App Store',
      description: 'Review submissions, approvals, and marketplace readiness.',
    };
  }

  if (pathname.startsWith('/subscriptions/plans')) {
    return {
      title: 'Subscription Plans',
      description: 'Review available subscription plans and default prices.',
    };
  }

  if (pathname.startsWith('/subscriptions')) {
    return {
      title: 'Subscription Companies',
      description: 'Review companies with a subscription.',
    };
  }

  return {
    title: 'Admin Portal',
    description: 'Manage the platform with a clear view of operational priorities.',
  };
}
